import { ApolloLink, FetchResult, Observable } from "@apollo/client";
import stableStringify from "fast-json-stable-stringify";

const DEFAULT_TTL = 10 * 60 * 1000; // 10 minutes

interface CacheEntry {
  data: FetchResult;
  timestamp: number;
}

const cache = new Map<string, CacheEntry>();

function getCacheKey(operationName: string, variables?: Record<string, any>): string {
  return operationName + "::" + stableStringify(variables ?? {});
}

/**
 * Apollo Link that implements TTL-based response caching.
 *
 * Opt-in per query via context:
 *   useQuery(MY_QUERY, { context: { ttl: 10 * 60 * 1000 } })
 *
 * How it works:
 * - First request goes to the server, response is cached with a timestamp
 * - Subsequent requests within the TTL window return the cached response immediately
 *   (no network request, no auth chain, no server hit)
 * - After TTL expires, the next request goes to the server and refreshes the cache
 * - Queries without context.ttl are unaffected
 *
 * To force-invalidate after a mutation:
 *   invalidateTTLCache("operationName")
 */
export function createTTLCacheLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const context = operation.getContext();
    const ttl: number | undefined = context.ttl ?? (context.ttl === undefined ? undefined : DEFAULT_TTL);

    // No TTL → pass through unchanged
    if (ttl === undefined) {
      return forward(operation);
    }

    // Only cache queries
    const definition = operation.query.definitions[0];
    if (!definition || !("operation" in definition) || definition.operation !== "query") {
      return forward(operation);
    }

    const key = getCacheKey(operation.operationName, operation.variables);
    const entry = cache.get(key);
    const now = Date.now();

    if (entry && now - entry.timestamp < ttl) {
      return new Observable((observer) => {
        observer.next(entry.data);
        observer.complete();
      });
    }

    return forward(operation).map((result) => {
      cache.set(key, { data: result, timestamp: Date.now() });
      return result;
    });
  });
}

/**
 * Force-invalidate cached TTL entries (e.g., after a mutation).
 * Without variables: invalidates all entries for that operation.
 * With variables: invalidates only the specific entry.
 */
export function invalidateTTLCache(operationName: string, variables?: Record<string, any>): void {
  if (variables) {
    const key = operationName + "::" + stableStringify(variables);
    cache.delete(key);
  } else {
    for (const key of cache.keys()) {
      if (key.startsWith(operationName + "::")) {
        cache.delete(key);
      }
    }
  }
}
